/**
 * DotGrid — canvas-based grid primitive for dot-matrix visualisations.
 *
 * Unified renderer for the swimlane, contrast, and hover co-occurrence grids.
 * Uses the same DPR-aware, draw-time-measured setup as ScopeColumns so text
 * stays crisp on resize.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useThemeVersion } from "../lib/theme.js";
import { observeSettledSize } from "../lib/settledResize.js";
import { truncateToWidth } from "../lib/canvasText.js";
import { TOOLTIP_LAYER } from "../lib/tooltipLayer.js";

function readTheme() {
  const s = getComputedStyle(document.documentElement);
  const g = (v) => s.getPropertyValue(v).trim();
  return {
    bg: g("--bg"),
    ink: g("--ink"),
    muted: g("--muted"),
    mutedSoft: g("--muted-soft"),
    line: g("--line"),
    lineSoft: g("--line-soft"),
    green: g("--green"),
    greenSoft: g("--green-soft"),
    controlBg: g("--control-bg"),
  };
}

function splitTwoLines(text) {
  if (!text) return ["", ""];
  const parts = text.trim().split(/\s+/);
  if (parts.length >= 2) {
    return [parts[0], parts.slice(1).join(" ")];
  }
  return [text, ""];
}

/**
 * Hit-test a pointer event against the grid and return a typed hit descriptor.
 * Returns null when the pointer is outside any interactive region.
 */
function hitTest({ x, y, labelW, headerH, cellW, rowH, rowTops, rows, cols }) {
  if (x < 0 || y < 0) return null;
  if (x < labelW && y < headerH) return null; // header/label corner
  if (y < headerH) {
    const ci = Math.floor((x - labelW) / cellW);
    if (ci < 0 || ci >= cols.length) return null;
    return { type: "col", ri: -1, ci, row: null, col: cols[ci] };
  }
  // Cumulative-tops lookup so variable-height rows hit-test correctly.
  // Falls back to the uniform-rowH path when rowTops isn't available
  // (callers from before per-row heights).
  let ri;
  if (rowTops && rowTops.length === rows.length + 1) {
    ri = -1;
    for (let i = 0; i < rows.length; i++) {
      if (y >= rowTops[i] && y < rowTops[i + 1]) { ri = i; break; }
    }
  } else {
    ri = Math.floor((y - headerH) / rowH);
  }
  if (x < labelW) {
    if (ri < 0 || ri >= rows.length) return null;
    return { type: "row", ri, ci: -1, row: rows[ri], col: null };
  }
  const ci = Math.floor((x - labelW) / cellW);
  if (ri < 0 || ri >= rows.length || ci < 0 || ci >= cols.length) return null;
  return { type: "cell", ri, ci, row: rows[ri], col: cols[ci] };
}

export default function DotGrid({
  rows,
  cols,
  cellW,
  rowH,
  labelW,
  headerH,
  colHeaderLines = 1,
  rowLabelAlign = "left",
  colHeaderFn = null,
  cellFn,
  // (ri, ci, row, col) => ReactNode | null. Returning non-null surfaces a
  // floating tooltip anchored just outside the cell on the right (flipped
  // to the left near the viewport edge). Cell tooltips are independent of
  // the label-overflow tooltip and can coexist with it on a single hover
  // (e.g. when a long row label and a cell are both hovered).
  cellTooltipFn = null,
  selfRowIndex = -1,
  selfColIndex = -1,
  hoveredRowIndex = -1,
  hoveredColIndex = -1,
  activeRowIndex = -1,
  activeColIndex = -1,
  onHover = null,
  onClick = null,
  className = "",
  fontFamily = "Inter, system-ui, sans-serif",
  // When true, rowH is used as-is (not clamped to fit canvas height);
  // content that exceeds the canvas scrolls on mousewheel.
  scrollable = false,
}) {
  const canvasRef = useRef(null);
  const themeVersion = useThemeVersion();
  const scrollYRef = useRef(0);

  // Cache the resolved CSS-var theme; readTheme() walks
  // getComputedStyle 9× and was visible in CPU profiles when called
  // per draw. Only refresh when themeVersion bumps (data-theme attr
  // change). First-render fallback handles the gap before the
  // useEffect populates the ref.
  const themeRef = useRef(null);
  if (themeRef.current === null) themeRef.current = readTheme();
  useEffect(() => {
    themeRef.current = readTheme();
  }, [themeVersion]);

  // Latest-props ref keeps the draw/hit-test closures stable without reattaching
  // listeners when props change mid-hover.
  const propsRef = useRef(null);
  propsRef.current = {
    rows, cols, cellW, rowH, labelW, headerH,
    colHeaderLines, rowLabelAlign, colHeaderFn,
    cellFn, cellTooltipFn,
    selfRowIndex, selfColIndex, hoveredRowIndex, hoveredColIndex,
    activeRowIndex, activeColIndex,
    fontFamily, scrollable,
    themeRef,
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const p = propsRef.current;
    const theme = p.themeRef.current;
    const greenRGB = theme.green;

    // Clear
    ctx.fillStyle = theme.bg;
    ctx.fillRect(0, 0, w, h);

    const rowCount = p.rows.length;
    const colCount = p.cols.length;
    // Clamp the requested cell width so the grid never bleeds past canvas
    // (borders on the container can shrink clientWidth below the caller's
    // assumed size by a couple of pixels). Row height is only clamped when
    // non-scrollable; scrollable grids keep the natural rowH and overflow
    // rows scroll on wheel.
    const drawCellW = colCount > 0
      ? Math.min(p.cellW, (w - p.labelW) / colCount)
      : p.cellW;
    // Default row height — clamped to fit when not scrollable. Per-row
    // overrides via `row.height` come next; we honor them in scrollable mode
    // (any overflow scrolls) and keep them in fit mode too (the caller is
    // responsible for not asking for more than the canvas can show).
    const baseRowH = p.scrollable
      ? p.rowH
      : (rowCount > 0 ? Math.min(p.rowH, (h - p.headerH) / rowCount) : p.rowH);

    // Cumulative top positions per row + per-row heights so variable-height
    // rows (cluster + shorter sub-rows) line up cleanly.
    const rowHeights = new Array(rowCount);
    const rowTops = new Array(rowCount + 1);
    const headerH = p.headerH;
    rowTops[0] = headerH;
    for (let ri = 0; ri < rowCount; ri++) {
      const requested = p.rows[ri]?.height;
      rowHeights[ri] = typeof requested === "number" && requested > 0 ? requested : baseRowH;
      rowTops[ri + 1] = rowTops[ri] + rowHeights[ri];
    }
    const contentBodyH = rowTops[rowCount] - headerH;

    // Scroll bounds: only meaningful when content > viewport.
    const viewportBodyH = Math.max(0, h - headerH);
    const maxScrollY = p.scrollable ? Math.max(0, contentBodyH - viewportBodyH) : 0;
    if (scrollYRef.current > maxScrollY) scrollYRef.current = maxScrollY;
    if (scrollYRef.current < 0) scrollYRef.current = 0;
    const scrollY = scrollYRef.current;

    const gridTop = headerH;
    const gridLeft = p.labelW;
    const gridRight = gridLeft + colCount * drawCellW;

    // Clip the body area so scrolled content doesn't paint over the header.
    ctx.save();
    if (p.scrollable) {
      ctx.beginPath();
      ctx.rect(0, gridTop, w, h - gridTop);
      ctx.clip();
    }

    // ── Expanded-cluster group background ────────────────────────────
    // A faint green tint behind a cluster row + its sub-rows so the group
    // reads as one bracketed unit. Drawn early so it sits beneath every
    // other highlight and behind cell content.
    for (let ri = 0; ri < rowCount; ri++) {
      const row = p.rows[ri];
      if (row?.kind !== "cluster" || !row.expanded) continue;
      let endRi = ri;
      while (endRi + 1 < rowCount) {
        const nextKind = p.rows[endRi + 1]?.kind;
        if (nextKind !== "sub" && nextKind !== "subSpacer") break;
        endRi++;
      }
      if (endRi === ri) continue;
      const top = rowTops[ri] - scrollY;
      const bottom = rowTops[endRi + 1] - scrollY;
      if (bottom < gridTop || top > h) continue;
      const clipTop = Math.max(gridTop, top);
      const clipBottom = Math.min(h, bottom);
      if (clipBottom <= clipTop) continue;
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = greenRGB;
      ctx.fillRect(0, clipTop, w, clipBottom - clipTop);
      ctx.globalAlpha = 1;

      // Tiger stripes — every other sub-row gets a touch more green tint so
      // adjacent endpoint labels are easier to scan apart. Inset on both
      // sides so the stripe doesn't read as a full row background like the
      // cluster header.
      const stripeLeft = 4;
      const stripeRight = w - 4;
      const stripeW = Math.max(0, stripeRight - stripeLeft);
      let subIdx = 0;
      for (let r = ri + 1; r <= endRi; r++) {
        if (p.rows[r]?.kind !== "sub") continue;
        if (subIdx % 2 === 1 && stripeW > 0) {
          const sTop = rowTops[r] - scrollY;
          const sH = rowHeights[r];
          const sClipTop = Math.max(gridTop, sTop);
          const sClipBottom = Math.min(h, sTop + sH);
          if (sClipBottom > sClipTop) {
            ctx.globalAlpha = 0.04;
            ctx.fillStyle = greenRGB;
            ctx.fillRect(stripeLeft, sClipTop, stripeW, sClipBottom - sClipTop);
            ctx.globalAlpha = 1;
          }
        }
        subIdx++;
      }

      // Soft 1px hairline at the bottom of the group to seal it off from the
      // next cluster row — `--line-soft` so it stays subordinate to the green
      // tint above and never competes with cell content.
      ctx.strokeStyle = theme.lineSoft || theme.line;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, clipBottom - 0.5);
      ctx.lineTo(w, clipBottom - 0.5);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // ── Self row/col highlights (drawn beneath everything) ───────────
    if (p.selfRowIndex >= 0 && p.selfRowIndex < rowCount) {
      ctx.globalAlpha = 0.14;
      ctx.fillStyle = greenRGB;
      ctx.fillRect(0, rowTops[p.selfRowIndex] - scrollY, gridRight, rowHeights[p.selfRowIndex]);
      ctx.globalAlpha = 1;
    }
    if (p.selfColIndex >= 0 && p.selfColIndex < colCount) {
      ctx.globalAlpha = 0.14;
      ctx.fillStyle = greenRGB;
      ctx.fillRect(gridLeft + p.selfColIndex * drawCellW, gridTop, drawCellW, h - gridTop);
      ctx.globalAlpha = 1;
    }
    if (
      p.hoveredColIndex >= 0 &&
      p.hoveredColIndex < colCount &&
      p.hoveredColIndex !== p.selfColIndex
    ) {
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = theme.greenSoft || greenRGB;
      ctx.fillRect(gridLeft + p.hoveredColIndex * drawCellW, gridTop, drawCellW, h - gridTop);
      ctx.globalAlpha = 1;
    }
    if (
      p.hoveredRowIndex >= 0 &&
      p.hoveredRowIndex < rowCount &&
      p.hoveredRowIndex !== p.selfRowIndex
    ) {
      const hy = rowTops[p.hoveredRowIndex] - scrollY;
      const hh = rowHeights[p.hoveredRowIndex];
      if (hy + hh >= gridTop && hy <= h) {
        ctx.globalAlpha = 0.22;
        ctx.fillStyle = theme.greenSoft || greenRGB;
        ctx.fillRect(0, hy, gridRight, hh);
        ctx.globalAlpha = 1;
      }
    }

    // ── Active-scope highlight (drawn as an outline so it reads as "you
    //    are here" without competing with self's green fill). ────────────
    const drawActiveOutline = (x, y, w2, h2) => {
      ctx.strokeStyle = greenRGB;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.9;
      ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, w2 - 1), Math.max(0, h2 - 1));
      ctx.globalAlpha = 1;
    };
    if (p.activeRowIndex >= 0 && p.activeRowIndex < rowCount) {
      const ay = rowTops[p.activeRowIndex] - scrollY;
      const ah = rowHeights[p.activeRowIndex];
      if (ay + ah >= gridTop && ay <= h) {
        drawActiveOutline(0, ay, gridRight, ah);
      }
    }
    if (p.activeColIndex >= 0 && p.activeColIndex < colCount) {
      drawActiveOutline(gridLeft + p.activeColIndex * drawCellW, gridTop, drawCellW, h - gridTop);
    }

    // ── Row labels ────────────────────────────────────────────────────
    // Match TraceRow rhythm: 11px at line-height ~1.08. Sub-rows now share
    // the same label font size as cluster rows — only the row height and
    // text color differentiate them.
    const labelFontSize = 11;
    ctx.textBaseline = "middle";
    for (let ri = 0; ri < rowCount; ri++) {
      const row = p.rows[ri];
      if (!row.name) continue;
      const isSelf = ri === p.selfRowIndex;
      const isPad = row.isPadding;
      const isSub = row.kind === "sub";
      const indent = row.indent || 0;
      const rowH_i = rowHeights[ri];
      const y = rowTops[ri] + rowH_i / 2 - scrollY;
      if (y < gridTop - rowH_i || y > h + rowH_i) continue;

      ctx.font = `${isSelf && !isSub ? 600 : 400} ${labelFontSize}px ${p.fontFamily}`;
      ctx.fillStyle = isPad
        ? theme.mutedSoft
        : isSub
          ? theme.muted
          : (isSelf ? theme.ink : theme.muted);
      ctx.globalAlpha = isPad ? 0.5 : 1;

      if (p.rowLabelAlign === "right") {
        ctx.textAlign = "right";
        const maxW = p.labelW - 10 - indent;
        ctx.fillText(truncateToWidth(ctx, row.name, maxW), p.labelW - 8, y + 0.5);
      } else {
        ctx.textAlign = "left";
        const labelX = 6 + indent;
        const maxW = p.labelW - 14 - indent;
        ctx.fillText(truncateToWidth(ctx, row.name, maxW), labelX, y + 0.5);

        // Sub-row spine + tick. The spine sits at a fixed offset near the
        // left edge so the horizontal tick can extend most of the indent
        // distance — long horizontal + short vertical reads as a clean
        // bracket rather than a disproportionate stub.
        if (isSub && indent >= 10) {
          const spineX = 10;
          const tickEnd = 6 + indent - 4; // a small gap before the label
          const top = rowTops[ri] - scrollY;
          const bottom = top + rowH_i;
          const mid = y;
          const isLastSub = p.rows[ri + 1]?.kind !== "sub";
          ctx.strokeStyle = theme.mutedSoft || theme.muted;
          ctx.globalAlpha = 0.32;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(spineX + 0.5, top);
          ctx.lineTo(spineX + 0.5, isLastSub ? mid + 0.5 : bottom);
          ctx.moveTo(spineX + 0.5, mid + 0.5);
          ctx.lineTo(tickEnd + 0.5, mid + 0.5);
          ctx.stroke();
          ctx.globalAlpha = isPad ? 0.5 : 1;
        }
      }
      ctx.globalAlpha = 1;
    }

    // ── Cells ─────────────────────────────────────────────────────────
    for (let ri = 0; ri < rowCount; ri++) {
      const row = p.rows[ri];
      const rowH_i = rowHeights[ri];
      const rowTop = rowTops[ri] - scrollY;
      if (rowTop + rowH_i < gridTop || rowTop > h) continue;
      const cy = rowTop + rowH_i / 2;
      const cellTop = rowTop + 1;
      const cellBgH = Math.max(1, rowH_i - 2);

      for (let ci = 0; ci < colCount; ci++) {
        const col = p.cols[ci];
        const cell = p.cellFn ? p.cellFn(ri, ci, row, col, { cellW: drawCellW, rowH: rowH_i }) : null;
        if (!cell) continue;

        const cx = gridLeft + (ci + 0.5) * drawCellW;
        const x = gridLeft + ci * drawCellW;

        if (cell.shade && cell.shade.opacity > 0) {
          ctx.globalAlpha = Math.min(1, cell.shade.opacity);
          ctx.fillStyle = cell.shade.color || greenRGB;
          ctx.fillRect(x, cellTop, drawCellW, cellBgH);
          ctx.globalAlpha = 1;
        }

        if (cell.dot && cell.dot.r > 0) {
          ctx.globalAlpha = Math.min(1, cell.dot.opacity ?? 1);
          ctx.fillStyle = cell.dot.color || greenRGB;
          ctx.beginPath();
          ctx.arc(cx, cy, cell.dot.r, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        } else if (cell.placeholder) {
          const pr = cell.placeholder.r ?? 1.2;
          ctx.globalAlpha = cell.placeholder.opacity ?? 0.18;
          ctx.fillStyle = theme.muted;
          ctx.beginPath();
          ctx.arc(cx, cy, pr, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }
    }

    ctx.restore();  // release body clip

    // ── Column headers (drawn last so scrolled rows never overlap) ───
    if (p.headerH > 0) {
      // Cover the header strip with the bg so scrolled content doesn't peek through.
      ctx.fillStyle = theme.bg;
      ctx.fillRect(0, 0, w, p.headerH);

      const headerFontSize = 10.5;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = theme.muted;

      for (let ci = 0; ci < colCount; ci++) {
        const col = p.cols[ci];
        const cx = gridLeft + (ci + 0.5) * drawCellW;
        const isSelf = ci === p.selfColIndex;
        const raw = p.colHeaderFn ? p.colHeaderFn(ci, col) : col.name;
        if (!raw) continue;

        ctx.fillStyle = isSelf ? theme.ink : theme.muted;
        ctx.font = `${isSelf ? 600 : 400} ${headerFontSize}px ${p.fontFamily}`;

        if (p.colHeaderLines === 2) {
          const maxW = drawCellW - 4;
          const [a, b] = splitTwoLines(raw);
          const line1 = truncateToWidth(ctx, a, maxW);
          const line2 = truncateToWidth(ctx, b, maxW);
          if (line2) {
            ctx.fillText(line1, cx, p.headerH - 22);
            ctx.fillText(line2, cx, p.headerH - 9);
          } else {
            ctx.fillText(line1, cx, p.headerH - 14);
          }
        } else {
          ctx.fillText(raw, cx, p.headerH - 10);
        }
      }
    }

    // ── Scroll indicator (thin track on right when scrollable + overflow) ──
    if (p.scrollable && maxScrollY > 0) {
      const trackH = h - p.headerH;
      const thumbRatio = viewportBodyH / contentBodyH;
      const thumbH = Math.max(20, trackH * thumbRatio);
      const thumbY = p.headerH + (trackH - thumbH) * (scrollY / maxScrollY);
      ctx.fillStyle = theme.line;
      ctx.globalAlpha = 0.35;
      ctx.fillRect(w - 3, thumbY, 2, thumbH);
      ctx.globalAlpha = 1;
    }
  }, []);

  // Resize observer → redraw, burst-coalesced (O-1996). A divider drag
  // resizes the grid every frame, and a full label+dot repaint with a
  // backing-store resize per tick is a per-frame texture upload. Grids
  // are text-labeled, so mid-burst the canvas pins to the size it was
  // last drawn at and the parent clips it (clip, don't stretch — same
  // policy as ScopeColumns); the settled tick restores the 100% sizing
  // and redraws crisp. The restore value must be exactly "100%": React
  // owns width/height via the style prop and won't rewrite a value it
  // believes is already applied, so clearing to "" would strand the
  // canvas unsized.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    draw();
    return observeSettledSize(
      canvas,
      () => {
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        draw();
      },
      {
        onBurstStart: () => {
          const dpr = window.devicePixelRatio || 1;
          canvas.style.width = `${Math.round(canvas.width / dpr)}px`;
          canvas.style.height = `${Math.round(canvas.height / dpr)}px`;
        },
      },
    );
  }, [draw]);

  // Re-draw once fonts finish loading (measureText uses fallback metrics
  // until then, which can truncate labels that would otherwise fit).
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts?.ready) return;
    let cancelled = false;
    document.fonts.ready.then(() => { if (!cancelled) draw(); });
    return () => { cancelled = true; };
  }, [draw]);

  // Re-draw when inputs change. Both hover indexes and both active indexes
  // must be in the dep list so crosshair highlights repaint on every move.
  useEffect(() => {
    draw();
  }, [rows, cols, cellW, rowH, labelW, headerH, colHeaderLines, rowLabelAlign, cellFn, colHeaderFn, selfRowIndex, selfColIndex, hoveredRowIndex, hoveredColIndex, activeRowIndex, activeColIndex, themeVersion, draw]);

  // Theme reactivity is shared via lib/theme.js → useThemeVersion()
  // (single module-level MutationObserver across all consumers).

  const getHit = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const p = propsRef.current;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const drawCellW = p.cols.length > 0
      ? Math.min(p.cellW, (w - p.labelW) / p.cols.length)
      : p.cellW;
    // Mirror draw()'s row-height resolution so hit testing aligns with what's
    // on screen — uniform default with optional per-row overrides via row.height.
    const baseRowH = p.scrollable
      ? p.rowH
      : (p.rows.length > 0 ? Math.min(p.rowH, (h - p.headerH) / p.rows.length) : p.rowH);
    const rowTops = new Array(p.rows.length + 1);
    rowTops[0] = p.headerH;
    for (let i = 0; i < p.rows.length; i++) {
      const requested = p.rows[i]?.height;
      const rh = typeof requested === "number" && requested > 0 ? requested : baseRowH;
      rowTops[i + 1] = rowTops[i] + rh;
    }
    // Header hits don't scroll; body hits do.
    const adjustedY = y < p.headerH ? y : y + scrollYRef.current;
    return hitTest({
      x, y: adjustedY,
      labelW: p.labelW,
      headerH: p.headerH,
      cellW: drawCellW,
      rowH: baseRowH,
      rowTops,
      rows: p.rows,
      cols: p.cols,
    });
  }, []);

  const handleWheel = useCallback((event) => {
    const p = propsRef.current;
    if (!p.scrollable) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const viewportBodyH = Math.max(0, canvas.clientHeight - p.headerH);
    let contentBodyH = 0;
    for (const row of p.rows) {
      const requested = row?.height;
      contentBodyH += typeof requested === "number" && requested > 0 ? requested : p.rowH;
    }
    const maxScrollY = Math.max(0, contentBodyH - viewportBodyH);
    if (maxScrollY <= 0) return;
    const next = Math.max(0, Math.min(maxScrollY, scrollYRef.current + event.deltaY));
    if (next !== scrollYRef.current) {
      scrollYRef.current = next;
      event.preventDefault();
      // Tooltip was anchored to a row that just moved — drop it and
      // let the next mouse move re-anchor at the new spot.
      setOverflowTooltip(null);
      draw();
    }
  }, [draw]);

  // Overflow tooltip for row labels + 2-line column headers. Mirrors
  // the draw fn's truncateToWidth budgets and fonts so detection
  // matches whatever ctx.fillText would visibly truncate. 1-line col
  // headers aren't truncated by the drawer, so we skip them.
  const [overflowTooltip, setOverflowTooltip] = useState(null);
  const measureCtxRef = useRef(null);
  const getMeasureCtx = useCallback(() => {
    if (!measureCtxRef.current) {
      measureCtxRef.current = document.createElement("canvas").getContext("2d");
    }
    return measureCtxRef.current;
  }, []);

  const computeOverflowTooltip = useCallback((hit) => {
    if (!hit) return null;
    if (hit.row?.isPadding || hit.col?.isPadding) return null;
    const p = propsRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const mctx = getMeasureCtx();

    const clampLeft = (px) => Math.min(
      Math.max(8, px),
      window.innerWidth - 420 - 8,
    );

    if (hit.type === "row") {
      const row = hit.row;
      const text = row?.name ? String(row.name) : "";
      if (!text) return null;
      const indent = row.indent || 0;
      const rightAligned = p.rowLabelAlign === "right";
      const maxW = rightAligned
        ? p.labelW - 10 - indent
        : p.labelW - 14 - indent;
      const isSelf = hit.ri === p.selfRowIndex;
      const isSub = row.kind === "sub";
      mctx.font = `${isSelf && !isSub ? 600 : 400} 11px ${p.fontFamily}`;
      if (mctx.measureText(text).width <= maxW) return null;

      const baseRowH = p.scrollable
        ? p.rowH
        : (p.rows.length > 0 ? Math.min(p.rowH, (h - p.headerH) / p.rows.length) : p.rowH);
      let rowTop = p.headerH;
      for (let i = 0; i < hit.ri; i++) {
        const requested = p.rows[i]?.height;
        rowTop += typeof requested === "number" && requested > 0 ? requested : baseRowH;
      }
      const requested = p.rows[hit.ri]?.height;
      const rowH_i = typeof requested === "number" && requested > 0 ? requested : baseRowH;
      const yOnCanvas = rowTop - scrollYRef.current;
      // Anchor at the same x the drawer used: left edge of the label
      // for left-aligned rows, right edge for right-aligned (matrix
      // views), with translateX(-100%) on the JSX side so the tooltip
      // extends leftward from there.
      return rightAligned
        ? {
            left: rect.left + p.labelW - 8,
            top: rect.top + yOnCanvas + rowH_i + 4,
            text,
            rightAnchor: true,
          }
        : {
            left: clampLeft(rect.left + 6 + indent),
            top: rect.top + yOnCanvas + rowH_i + 4,
            text,
          };
    }

    if (hit.type === "col") {
      if (p.colHeaderLines !== 2) return null;
      const text = p.colHeaderFn ? p.colHeaderFn(hit.ci, hit.col) : hit.col?.name;
      if (!text) return null;
      const drawCellW = p.cols.length > 0
        ? Math.min(p.cellW, (w - p.labelW) / p.cols.length)
        : p.cellW;
      const maxW = drawCellW - 4;
      const isSelf = hit.ci === p.selfColIndex;
      mctx.font = `${isSelf ? 600 : 400} 10.5px ${p.fontFamily}`;
      const [a, b] = splitTwoLines(String(text));
      if (mctx.measureText(a).width <= maxW && mctx.measureText(b).width <= maxW) return null;
      const cx = p.labelW + (hit.ci + 0.5) * drawCellW;
      // Anchor at the column's horizontal center and rely on CSS
      // translateX(-50%) to centre the tooltip — narrow columns
      // would otherwise pile up at the same clamped left edge.
      return {
        left: rect.left + cx,
        top: rect.top + p.headerH + 4,
        text: String(text),
        centered: true,
      };
    }

    return null;
  }, [getMeasureCtx]);

  // Cell tooltip: floats next to the hovered cell, content supplied by the
  // consumer via cellTooltipFn. Anchored to the cell's bounding box, not
  // the cursor, so the tooltip doesn't jitter on mouse movement within the
  // same cell. Position computed from the same cellW/rowH the drawer uses.
  const [cellTooltip, setCellTooltip] = useState(null);
  const computeCellTooltip = useCallback((hit) => {
    if (!hit || hit.type !== "cell") return null;
    if (hit.row?.isPadding || hit.col?.isPadding) return null;
    const p = propsRef.current;
    if (!p.cellTooltipFn) return null;
    const content = p.cellTooltipFn(hit.ri, hit.ci, hit.row, hit.col);
    if (!content) return null;
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const drawCellW = p.cols.length > 0
      ? Math.min(p.cellW, (w - p.labelW) / p.cols.length)
      : p.cellW;
    const baseRowH = p.scrollable
      ? p.rowH
      : (p.rows.length > 0 ? Math.min(p.rowH, (h - p.headerH) / p.rows.length) : p.rowH);
    // Mirror draw()'s per-row height resolution.
    let rowTop = p.headerH;
    for (let i = 0; i < hit.ri; i++) {
      const requested = p.rows[i]?.height;
      rowTop += typeof requested === "number" && requested > 0 ? requested : baseRowH;
    }
    const requested = p.rows[hit.ri]?.height;
    const rowH_i = typeof requested === "number" && requested > 0 ? requested : baseRowH;
    const cellLeft  = p.labelW + hit.ci * drawCellW;
    const cellRight = cellLeft + drawCellW;
    const cellMidY  = rowTop + rowH_i / 2 - scrollYRef.current;
    // Prefer right-of-cell; flip left if a ~240px tooltip would overflow.
    const TOOLTIP_W = 240;
    const margin = 8;
    const rightAnchor =
      rect.left + cellRight + TOOLTIP_W + margin > window.innerWidth;
    return {
      left: rect.left + (rightAnchor ? cellLeft - 6 : cellRight + 6),
      top: rect.top + cellMidY,
      content,
      rightAnchor,
    };
  }, []);

  // Gate both setOverflowTooltip AND onHover on row/col transitions.
  // mousemove fires many times per frame; without the gate, every
  // tick forces a React re-render (tooltip portal + every consumer
  // that setStates the hit — ClusterSwimlane, ContrastPanel,
  // ContextPanel all do this unconditionally) even when the visible
  // hover state is identical. Visible in CPU profiles as Li-time
  // and per-render setAttribute/appendChild churn.
  const lastHitKeyRef = useRef("");
  const handleMouseMove = useCallback((event) => {
    const hit = getHit(event);
    const key = hit ? `${hit.type}:${hit.ri}:${hit.ci}` : "";
    if (key === lastHitKeyRef.current) return;
    lastHitKeyRef.current = key;
    setOverflowTooltip(computeOverflowTooltip(hit));
    setCellTooltip(computeCellTooltip(hit));
    if (!onHover) return;
    if (hit && (hit.row?.isPadding || hit.col?.isPadding)) {
      onHover(null);
      return;
    }
    onHover(hit);
  }, [onHover, getHit, computeOverflowTooltip, computeCellTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (lastHitKeyRef.current === "") return;
    lastHitKeyRef.current = "";
    setOverflowTooltip(null);
    setCellTooltip(null);
    if (onHover) onHover(null);
  }, [onHover]);

  const handleClick = useCallback((event) => {
    if (!onClick) return;
    const hit = getHit(event);
    if (!hit) return;
    if (hit.row?.isPadding || hit.col?.isPadding) return;
    onClick(hit);
  }, [onClick, getHit]);

  const cursor = onClick ? "pointer" : "default";

  // Attach a non-passive wheel listener so preventDefault works (React's
  // onWheel is passive by default since React 17).
  useEffect(() => {
    if (!scrollable) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [scrollable, handleWheel]);

  return (
    <>
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: "100%", height: "100%", display: "block", cursor }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick ? handleClick : undefined}
    />
    {overflowTooltip ? createPortal(
      <div
        role="tooltip"
        className={`pointer-events-none fixed ${TOOLTIP_LAYER} whitespace-normal break-words border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--ink)] shadow-lg`}
        style={{
          top: `${overflowTooltip.top}px`,
          left: `${overflowTooltip.left}px`,
          maxWidth: "420px",
          transform: overflowTooltip.centered
            ? "translateX(-50%)"
            : overflowTooltip.rightAnchor
              ? "translateX(-100%)"
              : undefined,
        }}
      >
        {overflowTooltip.text}
      </div>,
      document.body,
    ) : null}
    {cellTooltip ? createPortal(
      <div
        role="tooltip"
        className={`pointer-events-none fixed ${TOOLTIP_LAYER} border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[10px] py-[8px] text-[11px] leading-[1.35] text-[color:var(--ink)] shadow-[0_4px_12px_rgba(0,0,0,0.25)]`}
        style={{
          top: `${cellTooltip.top}px`,
          left: `${cellTooltip.left}px`,
          maxWidth: "260px",
          // Vertically center on the cell; right-anchor pulls the box left
          // of its anchor so it grows toward the viewport edge it has room
          // for, not past it.
          transform: cellTooltip.rightAnchor
            ? "translate(-100%, -50%)"
            : "translateY(-50%)",
        }}
      >
        {cellTooltip.content}
      </div>,
      document.body,
    ) : null}
    </>
  );
}
